import { Drone, Viewer } from "@nova/schemas"
import { getColorFromId } from "@nova/util-strings"
import classNames from "classnames"
import { HTMLAttributes, useCallback, useState } from "react"
import * as Icons from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { openVideoFlight } from "plugins/Video/state/thunks"
import { ProfilePhoto } from "../../settings-and-collaborators/profile/ProfilePhoto"
import { selectActivatingProjectId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { openPreparingToFollowNotification } from "user/profile/slice"
import { prepareToWatch } from "features/pubsub/slice"
import { useActivateProject } from "features/data-management/workspace/context/navigation"

const MAX_ICONS = 5

export const WorkspaceCardViewers = ({
    viewers,
    drones,
    ...rest
}: {
    viewers: Viewer[]
    drones?: Drone[]
} & HTMLAttributes<HTMLDivElement>) => {
    const dispatch = useAppDispatch()
    const [hoveredId, setHoveredId] = useState<string>()
    const isActivatingSomeProject = !!useAppSelector(selectActivatingProjectId())
    const handleActivateProject = useActivateProject()

    const subscribeToLiveDrone = useCallback(
        async (projectId: string, flightId: string) => {
            await handleActivateProject(projectId)
            await new Promise<void>((resolve) => setTimeout(resolve, 0))
            dispatch(openVideoFlight({ flightId }))
        },
        [dispatch, handleActivateProject]
    )

    const subscribeToViewer = useCallback(
        async (viewer: Viewer) => {
            dispatch(openPreparingToFollowNotification(viewer))
            dispatch(prepareToWatch(viewer))
            await handleActivateProject(viewer.projectId)
        },
        [dispatch, handleActivateProject]
    )

    return (
        <div {...rest} className={classNames("absolute", rest.className)}>
            <div className="relative flex">
                {/* +X Viewers */}
                {viewers.length > 3 && (
                    <div
                        className={classNames(
                            "relative select-none w-6 h-6 bg-gray-300 -ml-1 shadow-dp3 rounded-md flex items-center justify-center text-gray-0",
                            {
                                "brightness-50": hoveredId,
                            }
                        )}
                    >
                        <small className="xs ml-px">{`+${viewers.length - 3}`}</small>
                    </div>
                )}

                {/* Viewers */}
                {viewers
                    .slice(0, MAX_ICONS - (Array.isArray(drones) ? drones?.length || 0 : 0))
                    .map((viewer, i) => (
                        <div
                            key={`profile_${i}`}
                            className={classNames({
                                "z-[150]": hoveredId === `profile_${i}`,
                                "brightness-50": hoveredId && hoveredId !== `profile_${i}`,
                            })}
                        >
                            <div
                                className="w-6 min-w-[24px] aspect-square rounded-md overflow-hidden shadow-dp3 -ml-1"
                                onMouseEnter={() => setHoveredId(`profile_${i}`)}
                                onMouseLeave={() => setHoveredId(undefined)}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (isActivatingSomeProject) return
                                    subscribeToViewer(viewer)
                                }}
                            >
                                <ProfilePhoto userId={viewer.userId} fullName={viewer.name} />
                            </div>
                        </div>
                    ))}

                {/* Drones */}
                {Array.isArray(drones) &&
                    drones?.map((drone, i) => {
                        const color = getColorFromId(drone.createdBy.userId)

                        return (
                            <div
                                key={`drone_${i}`}
                                style={{ backgroundColor: color }}
                                className={classNames(
                                    "relative select-none w-6 h-6 -ml-1 shadow-dp3 rounded-md flex items-center justify-center text-gray-0",
                                    {
                                        "z-[100]": hoveredId === `drone_${i}`,
                                        "brightness-50 z-50":
                                            hoveredId && hoveredId !== `drone_${i}`,
                                    }
                                )}
                                onMouseEnter={() => {
                                    if (!isActivatingSomeProject) setHoveredId(`drone_${i}`)
                                }}
                                onMouseLeave={() => {
                                    if (!isActivatingSomeProject) setHoveredId(undefined)
                                }}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (isActivatingSomeProject) return
                                    subscribeToLiveDrone(drone.projectId, drone.flightId)
                                }}
                            >
                                <Icons.ExtraSmallIcon Icon={Icons.LiveDrone} />
                                <svg
                                    viewBox="0 0 5 5"
                                    className="absolute top-[0.1875rem] right-[0.1875rem] w-1 h-1"
                                >
                                    <ellipse
                                        cx="2.5"
                                        cy="2.5"
                                        rx="1.5"
                                        ry="1.5"
                                        fill="#FF3B30"
                                        stroke="#FF3B30BF"
                                        strokeWidth="1"
                                    />
                                </svg>
                            </div>
                        )
                    })}
            </div>
        </div>
    )
}
